import ReplayIcon from '@mui/icons-material/Replay'
import {Tooltip} from '@mui/material'
import {Box, Typography} from '@viptech/react-components'
import {capitalize} from 'lodash'
import moment from 'moment'
import {useSnackbar} from 'notistack'
import {useEffect, useState} from 'react'
import {useParams} from 'react-router-dom'
import {ConfirmationModal} from 'src/components/ConfirmationModal/ConfirmationModal'
import Loading from 'src/components/Loading'
import useGetCameraWarnings from 'src/hooks/api/camera/useGetCameraWarnings'
import useSingleCamera from 'src/hooks/api/camera/useSingleCamera'
import useSnapshot from 'src/hooks/api/camera/useSnapshot'
import useUpdateCamera from 'src/hooks/api/camera/useUpdateCamera'
import useUpdateCameraStatus from 'src/hooks/api/camera/useUpdateCameraStatus'
import useStartStream from 'src/hooks/api/streaming/useStartStream'
import externalLinkSvg from 'src/images/externalLinkSvg.svg'
import NoImage from 'src/images/noImage.svg'
import NoStream from 'src/images/NoStream.svg'

import useOutputServers from 'src/hooks/api/server/useOutputServers'
import {translateCameraWarnings} from 'src/pages/cameras/CameraFunctions'
import {CameraConfiguration} from 'src/services/api'
import handleErrorWithSnackbar from 'src/utilities/handleErrorWithSnackbar'
import {isResolutionValid} from 'src/utilities/regexValidation'
import Dropdown from '../../../../components/dropdown/Dropdown'
import {DropdownItem} from '../../../../components/dropdown/DropdownProps'
import {Input} from '../../../../components/input/Input'
import CameraConfigFooter from '../camera-config-footer/CameraConfigFooter'
import {ContainerBoxes, GreenSwitch} from './CameraGeneralDataBase'
import {LatLong} from './CameraGeneralDataProps'
import {
  CameraStatusLabel,
  ConectedCamStatus,
  ContainerGeneralData,
  ContainerStatusCamera,
  DivInfo,
  ImageToSnapshot,
  InfoConfigCamera,
  ReactPlayerContainer,
  SnapshotImageError,
  TitleConfigCamera,
  TitleModalConfig,
} from './styles'
import VideoPlayer from './VideoPlayer'

const allowedFPSConfigurationsValues = [
  {id: '10', label: '10 FPS'},
  {id: '15', label: '15 FPS'},
  {id: '20', label: '20 FPS'},
  {id: '25', label: '25 FPS'},
  {id: '30', label: '30 FPS'},
]

const DEFAULT_CAMERA_WIDTH = 1280
const DEFAULT_CAMERA_HEIGHT = 720

export function CameraGeneralData() {
  const [isCancel, setIsCancel] = useState(false)
  const [isEditing, setIsEditing] = useState(false)
  const [latLong, setLatLong] = useState<LatLong>({
    latitude: undefined,
    longitude: undefined,
  })
  const [isConfirmed, setIsConfirmed] = useState(false)

  const [cameraConfig, setCameraConfig] = useState<Partial<CameraConfiguration> | null>(null)
  const {id} = useParams<{id?: string}>()

  const {
    data: snapshot,
    isLoading: isLoadingSnapshot,
    refetch: refetchSnapshot,
    isError: isSnapshotError,
  } = useSnapshot({
    cameraId: Number(id),
    enabled: !!id,
  })

  const {data: camera} = useSingleCamera({
    onError: (error) => handleErrorWithSnackbar(enqueueSnackbar, error, 'Falha ao tentar obter câmera'),
    cameraId: Number(id),
    params: {includes: ['configuration', 'status', 'server', 'client', 'addressInfo', 'company']},
    enabled: !!id,
  })

  const {data: servers} = useOutputServers({
    params: {
      page: 1,
      pageSize: 20,
      filter: {integrationType: 'ENTRADA', companyIds: [camera?.companyId ?? -1], serverTypeIds: [2]},
      includes: ['serverType'],
    },
    enabled: !!camera,
  })

  const [cameraName, setCameraName] = useState<string>(camera?.name ?? '')

  const [openModal, setOpenModal] = useState<boolean>(false)
  const [modalContent, setModalContent] = useState<string | React.ReactElement>('')
  const [modalTitle, setModalTitle] = useState<string>('')

  const cameraSupportsLive = camera?.origin.includes('RTSP') || camera?.origin.includes('RTMP') || camera?.origin.includes('RTSP-DVR')
  const isCameraDigifort = camera && camera.origin.toLowerCase().includes('digifort')

  const {enqueueSnackbar} = useSnackbar()

  const {mutateAsync: getCamerasWarnings} = useGetCameraWarnings()
  const {mutateAsync: mutateCameraStatus, isPending: isLoadingCameraStatus} = useUpdateCameraStatus()
  const {mutateAsync: mutateUpdateCamera, isPending: isLoadingUpdateCamera} = useUpdateCamera()

  const {data: playerURL, refetch: refetchPlayer} = useStartStream({
    cameraId: camera?.id ?? -1,
    enabled: !!camera?.id && cameraSupportsLive,
  })

  async function handleSetCameraOnline(id: number) {
    const warnings = await getCamerasWarnings({cameraIds: [id]})

    if (warnings.length) {
      const reasons = translateCameraWarnings(warnings[0])

      const content = (
        <Box display='flex' direction='column' rowGap='10px'>
          <Typography variant='p'>Sua câmera foi desativada pelos seguintes motivos:</Typography>
          <ul>
            {reasons.map((reason) => (
              <li style={{paddingLeft: '-10px'}}>{reason}</li>
            ))}
          </ul>
          <Typography variant='p' weight={700}>
            Antes de prosseguir, confira se essas configurações foram corrigidas.
          </Typography>
        </Box>
      )
      return withConfirmation(`Habilitar câmera`, content)
    }
    return withConfirmation(`Habilitar câmera`, `Esta ação habilita a câmera a voltar a operar quando estiver armada.`)
  }

  function withConfirmation(title: string, content: string | React.ReactElement) {
    setModalContent(content)
    setOpenModal(true)
    setModalTitle(title)
  }

  const handleChangeStatus = async () => {
    if (!camera) return

    const isOnline = cameraConfig?.isOnline ?? false
    if (!isOnline) await handleSetCameraOnline(camera.id)
    else {
      await mutateCameraStatus({ids: [camera.id], status: false})
      setCameraConfig({isOnline: false})
    }
  }

  useEffect(() => {
    if (camera) setLatLong({longitude: camera.longitude ?? '-', latitude: camera.latitude ?? '-'})
  }, [camera, isCancel])

  useEffect(() => {
    if (cameraConfig && cameraConfig.snapshotWidth && cameraConfig?.snapshotHeight) {
      if (!isResolutionValid(cameraConfig?.snapshotWidth) || !isResolutionValid(cameraConfig?.snapshotHeight)) {
        setIsConfirmed(false)
        enqueueSnackbar('A largura e a altura devem estar entre 240 e 1920 pixels', {variant: 'warning'})
        return
      }
      setIsConfirmed(true)
    }
  }, [cameraConfig?.snapshotWidth, cameraConfig?.snapshotHeight])

  useEffect(() => {
    if (camera) {
      setCameraConfig({
        ...camera.configuration,
        snapshotHeight: camera.configuration.snapshotHeight ?? DEFAULT_CAMERA_HEIGHT,
        snapshotWidth: camera.configuration.snapshotWidth ?? DEFAULT_CAMERA_WIDTH,
      })
    }
    handleOpenLiveOrGetSnapshot()
  }, [camera])

  const handleOpenLiveOrGetSnapshot = () => {
    if (camera === null) return
    if (isCameraDigifort) return refetchSnapshot()
    setIsErrorOnStream(false)
    refetchPlayer()
    return
  }

  const [fpsSelected, setFpsSelected] = useState<DropdownItem>(
    allowedFPSConfigurationsValues.find((it) => it.id.includes(String(camera?.configuration.cameraFps) ?? '10 fps')) ?? {id: '10 FPS', label: '10 FPS'},
  )

  const [serverIdSelected, setServerIdSelected] = useState<DropdownItem>()

  useEffect(() => {
    const serverSelected = servers?.entities.find((it) => it.id === camera?.serverId)
    if (serverSelected) setServerIdSelected({id: serverSelected.id.toString(), label: serverSelected.name})
    else setServerIdSelected({id: '', label: ''})
  }, [servers, camera, isCancel])

  const [isErrorOnStream, setIsErrorOnStream] = useState(false)

  if (!camera) return
  return (
    <Box display='flex' direction='column' rowGap='20px'>
      <ContainerGeneralData>
        <ConfirmationModal
          content={modalContent}
          isOpen={openModal}
          title={modalTitle}
          onClose={() => setOpenModal(false)}
          buttonsContent={[
            {
              label: 'Cancelar',
              onClick: () => setOpenModal(false),
              variant: 'outlined',
              color: '#8E8E8E',
            },
            {
              label: 'Confirmar',
              onClick: async () => {
                setOpenModal(false)
                await mutateCameraStatus({ids: [camera.id], status: true})
                setCameraConfig({isOnline: true})
              },
              variant: 'contained',
              color: '#009EFF',
            },
          ]}
        />
        <ContainerBoxes>
          <ReactPlayerContainer>
            <Box display='flex' justify='flex-end' width='100%'>
              <Box display='flex' justifyContent='center' align='center' width='100%' height='100%' position='relative'>
                {isCameraDigifort && (
                  <Box
                    width='100%'
                    height='100%'
                    justifyContent='center'
                    align='center'
                    position='absolute'
                    display='flex'
                    style={{
                      transition: 'all 0.7s',
                      opacity: isLoadingSnapshot ? 1 : 0,
                    }}>
                    <Loading width='32px' height='32px' />
                  </Box>
                )}

                {cameraSupportsLive && playerURL && (
                  <Box maxWidth='700px' padding='10px' maxHeight='345px' width='100%' height='100%' display={!camera.origin.includes('RT') ? 'none' : 'block'}>
                    {!isErrorOnStream ? (
                      <VideoPlayer
                        src={playerURL}
                        thumbnail={snapshot ? `data:image/jpeg;base64,${snapshot.time.base64}` : undefined}
                        drawBarrierCoordinates={false}
                        setIsErrorOnStream={setIsErrorOnStream}
                      />
                    ) : (
                      <SnapshotImageError src={NoStream} alt='live error' />
                    )}
                  </Box>
                )}

                {cameraSupportsLive && !playerURL && !isCameraDigifort && (
                  <Box width='100%' height='100%' maxHeight='345px' display='flex' align='center' justifyContent='center' position='relative'>
                    {snapshot && <ImageToSnapshot src={`data:image/jpeg;base64,${snapshot.time.base64}`} alt='snapshot preview' style={{opacity: 0.35}} />}
                    <Box position='absolute'>
                      <Loading width='32px' height='32px' />
                    </Box>
                  </Box>
                )}

                {isSnapshotError && isCameraDigifort && <SnapshotImageError src={NoImage} alt='snapshot error' />}

                {!isSnapshotError && !isLoadingSnapshot && isCameraDigifort && (
                  <ImageToSnapshot src={snapshot ? `data:image/jpeg;base64,${snapshot.time.base64}` : NoImage} alt='Digifort snapshot' />
                )}
              </Box>
            </Box>

            <Tooltip title={isCameraDigifort ? 'Buscar imagem' : 'Iniciar transmissão ao vivo'} placement='top'>
              <button
                onClick={handleOpenLiveOrGetSnapshot}
                color='transparent'
                style={{
                  position: 'absolute',
                  right: '0',
                  background: 'none',
                  alignItems: 'flex-start',
                  display: 'flex',
                }}>
                <ReplayIcon color='primary' width={'80px'} style={{transform: 'scaleX(-1) rotate(0.85turn)', width: '25px'}} />
              </button>
            </Tooltip>
          </ReactPlayerContainer>
        </ContainerBoxes>

        <ContainerBoxes>
          <Box display='flex' direction='column'>
            <TitleModalConfig>Dados do Dispositivo</TitleModalConfig>

            <Box direction='column' display='flex' rowGap='30px' maxWidth='450px' minWidth='400px'>
              <Box direction='column' display='flex'>
                <TitleConfigCamera>Status</TitleConfigCamera>

                <ContainerStatusCamera>
                  <CameraStatusLabel status={camera.status.description}>{capitalize(camera.status.description)}</CameraStatusLabel>

                  <ConectedCamStatus isOnline={cameraConfig?.isOnline!}>
                    {isLoadingCameraStatus ? <Loading width='1.1em' height='1.1em' color='white' /> : <Box width='1.1em' />}
                    {cameraConfig && cameraConfig.isOnline ? 'Habilitada' : 'Desabilitada'}
                    <GreenSwitch
                      disabled={isLoadingCameraStatus}
                      inputProps={{'aria-label': 'controlled'}}
                      size='small'
                      onChange={() => handleChangeStatus()}
                      checked={!!cameraConfig && cameraConfig.isOnline}
                    />
                  </ConectedCamStatus>
                </ContainerStatusCamera>
              </Box>

              <Box direction='column' display='flex' rowGap='10px'>
                <DivInfo>
                  <Box display='flex' columnGap='10px' justifyContent='space-between'>
                    <Box>
                      <TitleConfigCamera>ID</TitleConfigCamera>
                      <InfoConfigCamera>{camera.id}</InfoConfigCamera>
                    </Box>

                    <Box display='flex' height='100%'>
                      {isEditing ? (
                        <Input.Root
                          labelFontSize='12px'
                          borderColor='#D0D5DD'
                          fontSize='12px'
                          width='250px'
                          height='32px'
                          onChange={(e) => setCameraName(e.target.value)}
                          label='Nome da câmera'
                          value={cameraName}
                          defaultValue={camera.name}
                          min={4}
                          max={200}
                          maxLength={200}
                        />
                      ) : (
                        <Box>
                          <TitleConfigCamera>Nome da câmera</TitleConfigCamera>
                          <InfoConfigCamera>{camera.name}</InfoConfigCamera>
                        </Box>
                      )}
                    </Box>
                  </Box>
                </DivInfo>

                <DivInfo>
                  <TitleConfigCamera>Origem</TitleConfigCamera>
                  <InfoConfigCamera>{camera.origin}</InfoConfigCamera>
                </DivInfo>

                {camera.server && (
                  <DivInfo>
                    {isEditing ? (
                      <Dropdown
                        labelFontSize='12px'
                        height='32px'
                        borderColor='#D0D5DD'
                        fontSize='12px'
                        label='Servidor de Entrada'
                        onChangeSelected={(e) => setServerIdSelected(e)}
                        selected={serverIdSelected}
                        items={
                          servers?.entities.map((server) => {
                            return {
                              id: String(server.id),
                              label: `${server.serverType?.description} - ${server.name}`,
                            }
                          }) ?? []
                        }
                      />
                    ) : (
                      <Box>
                        <TitleConfigCamera>Servidor de origem</TitleConfigCamera>
                        <Box display='flex' columnGap='5px'>
                          <InfoConfigCamera>{serverIdSelected?.label}</InfoConfigCamera>
                        </Box>
                      </Box>
                    )}
                  </DivInfo>
                )}

                <DivInfo style={{marginTop: isEditing ? '20px' : undefined}}>
                  <TitleConfigCamera>Atualizado em </TitleConfigCamera>
                  <InfoConfigCamera>{moment(camera.lastUpdate).format('DD/MM/YYYY HH:mm:ss')}</InfoConfigCamera>
                </DivInfo>

                <DivInfo>
                  <TitleConfigCamera>Empresa</TitleConfigCamera>
                  <InfoConfigCamera>{camera.company?.name}</InfoConfigCamera>
                </DivInfo>

                <DivInfo>
                  <TitleConfigCamera>Cliente</TitleConfigCamera>
                  {camera.client && (
                    <Box display='flex' columnGap='5px'>
                      <InfoConfigCamera onClick={() => window.open(`/registers/clients?id=${camera?.clientId}`)} cursor='pointer' textDecoration='underline'>
                        {camera.client?.name}
                      </InfoConfigCamera>
                      <img
                        src={externalLinkSvg}
                        alt='link para modal de cliente'
                        style={{width: '13px', cursor: 'pointer'}}
                        onClick={() => window.open(`/registers/clients?id=${camera?.clientId}`)}
                      />
                    </Box>
                  )}
                </DivInfo>
              </Box>
            </Box>
          </Box>
        </ContainerBoxes>

        <Box display='flex' direction='column' rowGap='20px' minWidth='200px' width='350px' style={{flex: '1 1 100%'}} justifyContent='space-between'>
          <ContainerBoxes>
            <TitleModalConfig>Localização</TitleModalConfig>

            <Input.Root
              labelFontSize='12px'
              borderColor='#D0D5DD'
              fontSize='12px'
              label='Latitude'
              value={String(latLong.latitude)}
              disabled={!isEditing}
              onChange={(e) => setLatLong((old) => ({...old, latitude: e.target.value}))}
              controlled
            />

            <Input.Root
              labelFontSize='12px'
              borderColor='#D0D5DD'
              fontSize='12px'
              label='Longitude'
              value={String(latLong.longitude)}
              onChange={(e) => setLatLong((old) => ({...old, longitude: e.target.value}))}
              disabled={!isEditing}
              controlled
            />
          </ContainerBoxes>
          {isCameraDigifort && (
            <ContainerBoxes>
              <TitleModalConfig>Tamanho da imagem</TitleModalConfig>

              <Input.Root
                required
                labelFontSize='12px'
                borderColor='#D0D5DD'
                fontSize='12px'
                label='Largura'
                type='number'
                hasError={!cameraConfig?.snapshotWidth || !isResolutionValid(cameraConfig?.snapshotWidth)}
                value={String(cameraConfig?.snapshotWidth)}
                controlled
                disabled={!isEditing}
                onChange={(e) => {
                  if (!isNaN(Number(e.target.value))) setCameraConfig((old) => ({...old, snapshotWidth: Number(e.target.value)}))
                }}
              />

              <Input.Root
                required
                labelFontSize='12px'
                borderColor='#D0D5DD'
                fontSize='12px'
                label='Altura'
                type='number'
                hasError={!cameraConfig?.snapshotHeight || !isResolutionValid(cameraConfig?.snapshotHeight)}
                value={String(cameraConfig?.snapshotHeight)}
                disabled={!isEditing}
                controlled
                onChange={(e) => {
                  if (!isNaN(Number(e.target.value))) setCameraConfig((old) => ({...old, snapshotHeight: Number(e.target.value)}))
                }}
              />
            </ContainerBoxes>
          )}

          <ContainerBoxes>
            <Box height={isCameraDigifort ? '100px' : '200px'} width='100%'>
              <TitleModalConfig>Câmera FPS</TitleModalConfig>

              {isEditing ? (
                <Dropdown
                  labelFontSize='12px'
                  height='32px'
                  borderColor='#D0D5DD'
                  fontSize='12px'
                  label='FPS configurado na sua câmera'
                  onChangeSelected={(e) => {
                    setFpsSelected(e)
                    setCameraConfig({cameraFps: Number(e.id)})
                  }}
                  selected={fpsSelected}
                  items={allowedFPSConfigurationsValues}
                />
              ) : (
                <Box>
                  <TitleConfigCamera>FPS configurado na sua câmera</TitleConfigCamera>
                  <InfoConfigCamera>{camera.configuration.cameraFps} FPS</InfoConfigCamera>
                </Box>
              )}
            </Box>
          </ContainerBoxes>
        </Box>
      </ContainerGeneralData>
      <CameraConfigFooter
        isEditing={isEditing}
        onEdit={() => {
          setIsEditing(true)
          setIsCancel(false)
        }}
        isLoading={isLoadingUpdateCamera}
        onCancel={() => {
          setIsEditing(false)
          setIsCancel(true)
          setCameraConfig({
            ...camera.configuration,
            snapshotHeight: camera.configuration.snapshotHeight ?? DEFAULT_CAMERA_HEIGHT,
            snapshotWidth: camera.configuration.snapshotWidth ?? DEFAULT_CAMERA_WIDTH,
          })
        }}
        disabled={!isConfirmed}
        onSubmit={async () => {
          if (!camera || !cameraConfig) return

          await mutateUpdateCamera({
            cameraId: camera.id,
            cameraConfig: cameraConfig,
            cameraName,
            latLong,
            serverId: serverIdSelected?.id !== '' ? Number(serverIdSelected?.id) : undefined,
          })
          setIsEditing(false)
          setIsCancel(false)
        }}
      />
    </Box>
  )
}
